import { createContext, useContext, useState, useCallback, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

// Endpoints qui NE DOIVENT PAS declencher un logout auto sur 401/403
// (permet a l'ecran d'erreur AuthCallback ou aux flows publics de gerer eux-memes)
const SKIP_LOGOUT_ENDPOINTS = [
  "/auth/login",
  "/auth/google/",
  "/auth/forgot-password",
  "/auth/reset-password",
];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem("odys_user");
    return stored ? JSON.parse(stored) : null;
  });

  const [token, setToken] = useState(() => localStorage.getItem("odys_token"));
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem("odys_token");
    localStorage.removeItem("odys_user");
    setToken(null);
    setUser(null);
  }, []);

  const updateUser = useCallback((updates) => {
    setUser((prev) => {
      const next = { ...(prev || {}), ...updates };
      localStorage.setItem("odys_user", JSON.stringify(next));
      return next;
    });
  }, []);

  const api = useCallback(() => {
    // Timeout par defaut 30s. Endpoints LLM appelants au cas par cas peuvent
    // preciser un timeout plus long via api().post(url, data, { timeout: 180000 }).
    const instance = axios.create({ baseURL: API, timeout: 30000 });
    if (token) {
      instance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
    instance.interceptors.response.use(
      (res) => res,
      (err) => {
        const url = err.config?.url || "";
        const status = err.response?.status;
        // Skip logout auto sur les endpoints publics d'auth
        const skip = SKIP_LOGOUT_ENDPOINTS.some((p) => url.includes(p));
        if ((status === 401 || status === 419) && !skip) {
          logout();
        }
        return Promise.reject(err);
      }
    );
    return instance;
  }, [token, logout]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    const data = res.data;
    localStorage.setItem("odys_token", data.token);
    localStorage.setItem("odys_user", JSON.stringify(data));
    setToken(data.token);
    setUser(data);
    return data;
  };

  // Refresh les infos user via /auth/me (utile apres accept-cgu ou changement CGU)
  const refreshMe = useCallback(async () => {
    const t = localStorage.getItem("odys_token");
    if (!t) return null;
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      const merged = { ...res.data, token: t };
      localStorage.setItem("odys_user", JSON.stringify(merged));
      setUser(merged);
      return merged;
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
      }
      return null;
    }
  }, [logout]);

  // Au mount : refresh les infos user MAIS skip si on est en callback OAuth
  useEffect(() => {
    // CRITICAL: skip /auth/me si returning from OAuth callback
    // (AuthCallback va echanger le session_id et etablir la session)
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    if (!token) {
      setLoading(false);
      return;
    }
    refreshMe().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, updateUser, refreshMe, api, setToken, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
